import React, { useContext } from "react"
import {
    Markdown,
    OutlinedButton,
    Text,
    Title,
    ToggleSwitchButton
} from "dash-styled"
import { Table } from "resha"
import { ReactComponent as AddIcon } from "icons/lib/plus.svg"
import { ReactComponent as TrashIcon } from "icons/lib/trash.svg"
import { ReactComponent as EditIcon } from "icons/lib/edit.svg"
import { ReactComponent as CopyIcon } from "icons/lib/copy.svg"
import { ReactComponent as DragIcon } from "icons/lib/drag.svg"

import {
    DataFieldType,
    FieldStatus
} from "../../../../../Models/DataProgram/DocumentExtraction"
import { randID } from "../../../../../Helpers/Functions/random"
import { SchemaFormDeleteModal } from "../../Modals"
import { FieldModal, FieldModalType } from "../Common/Modals/FieldModal"
import { DocumentModal } from "../Common/Modals/DocumentModal"
import {
    DocumentsListItemProps,
    useDocumentsListItem
} from "../Common/Hooks/useDocumentsListItem"
import { DocumentsExtractionContext } from "../DocumentsExtractionContext"

const IconWrapper =
    (Icon: React.FC<React.SVGProps<SVGSVGElement>>, noMargin = false) =>
    ({ className, ...rest }: React.SVGProps<SVGSVGElement>) =>
        <Icon className={`w-4 h-4  ${noMargin ? "" : className}`} {...rest} />

export const DocumentsListItem = ({
    schema,
    value: { documentId, documentType, annotationFields = [], ...restValue },
    onDelete,
    onEdit
}: DocumentsListItemProps) => {
    const {
        editDocumentState: [
            showEditDocument,
            openEditDocumentModal,
            closeEditDocumentModal
        ],
        editFieldState: [showFieldModal, , closeFieldModal],
        deleteModalState: [showDeleteModal, , closeDeleteModal],
        fieldModalOptionsState: [fieldModalOptions],
        deleteHandlerState: [deleteHandler],
        openDeleteDocument: openDeleteDocumentModal,
        openNewField,
        openFieldEdit,
        openFieldDelete,
        onDrop,
        onStatusChange,
        onFieldChange,
        onFieldDelete,
        onDocumentSubmit
    } = useDocumentsListItem({
        schema,
        value: {
            documentId,
            documentType,
            annotationFields,
            ...restValue
        },
        onDelete,
        onEdit
    })

    const { annotationsOutputList, setAnnotationsOutputList, findOutputId } =
        useContext(DocumentsExtractionContext)

    const handleFieldModalSubmit = (field: DataFieldType) => {
        const newList = annotationsOutputList
        if (newList) {
            const annotationsOutputId = findOutputId(field?.fieldId.data)
            // resetting field annotations on every submit
            if (annotationsOutputId) {
                setAnnotationsOutputList({
                    ...newList,
                    annotations: {
                        ...newList?.annotations,
                        [annotationsOutputId]: []
                    }
                })
            }
        }
        onFieldChange(field, fieldModalOptions)
    }

    const handleStatusChange =
        (
            type: "annotationFields" | "presetFields",
            prevValue: FieldStatus,
            index: number,
            fieldId: string
        ) =>
        () => {
            const newList = annotationsOutputList
            if (newList) {
                const annotationsOutputId = findOutputId(fieldId)
                if (annotationsOutputId) {
                    setAnnotationsOutputList({
                        ...newList,
                        annotations: {
                            ...newList?.annotations,
                            [annotationsOutputId]: []
                        }
                    })
                }
            }
            onStatusChange(type, prevValue, index)
        }

    return (
        <div className="mb-4 border border-solid rounded-lg">
            <div className="flex items-center bg-white p-2 pl-6 rounded-t-lg shadow">
                <Title
                    component="h4"
                    variant="heading4"
                    className="capitalize w-full m-0"
                >
                    {documentType}
                </Title>
                <OutlinedButton
                    Icon={EditIcon}
                    label="Edit"
                    marginLeft="2"
                    className="bg-white py-3"
                    onClick={openEditDocumentModal}
                />
                <OutlinedButton
                    Icon={TrashIcon}
                    label="Delete"
                    marginLeft="2"
                    className="bg-white py-3"
                    onClick={openDeleteDocumentModal}
                />
            </div>
            <div className="bg-smoke-20 p-6">
                <div className="flex mb-4">
                    <OutlinedButton
                        testId="add-new-field"
                        Icon={AddIcon}
                        label="Add new field"
                        marginLeft="2"
                        className="bg-smoke-20 py-3"
                        onClick={openNewField}
                    />
                </div>
                <Table
                    draggable
                    draggableColIndex={0}
                    className="w-full"
                    rowBgColor=""
                    style={{
                        borderSpacing: "0 4px"
                    }}
                    getKey={({ fieldId }) => fieldId.data}
                    items={annotationFields}
                    withBorders={false}
                    onDrop={onDrop}
                    columns={[
                        {
                            className:
                                "w-1/12 rounded-l-xl pl-2 bg-white border-b-rhino-300",
                            renderHeader: () => null,
                            render: () => {
                                return <DragIcon className="w-8 h-8" />
                            }
                        },
                        {
                            className: "w-4/12 bg-white border-b-rhino-300",
                            renderHeader: () => (
                                <Text color="gray">Name & Description</Text>
                            ),
                            headerClassName: "border-b-0",
                            render: ({ fieldName, description }) => {
                                return (
                                    <>
                                        <Title
                                            component="h5"
                                            variant="heading4"
                                            className="capitalize m-0"
                                        >
                                            {fieldName.data}
                                        </Title>
                                        <Markdown text={description.data} />
                                    </>
                                )
                            }
                        },
                        {
                            className: "w-2/12 bg-white border-b-rhino-300",
                            renderHeader: () => <Text color="gray">Type</Text>,
                            render: ({ dataType, options }) => {
                                return (
                                    <Text>
                                        {dataType?.data ||
                                            options?.data?.contentType}
                                    </Text>
                                )
                            }
                        },
                        {
                            className: "w-2/12 bg-white border-b-rhino-300",
                            renderHeader: () => (
                                <Text color="gray">Status</Text>
                            ),
                            render: ({ status, fieldId }, isChecked, index) => {
                                return (
                                    <div className="flex">
                                        <ToggleSwitchButton
                                            fullWidth={false}
                                            iconSize="lg"
                                            className="w-auto"
                                            offClass=""
                                            value={
                                                status.data ===
                                                FieldStatus.Active
                                            }
                                            activeColor="#00B092"
                                            inactiveColor="#EA5757"
                                            onChange={handleStatusChange(
                                                "annotationFields",
                                                status.data,
                                                index,
                                                fieldId.data
                                            )}
                                        />
                                        <Text>{status.data}</Text>
                                    </div>
                                )
                            }
                        },
                        {
                            className:
                                "w-3/12 rounded-r-xl pr-2 bg-white border-b-rhino-300",
                            renderHeader: () => null,
                            render: (value, isChecked, index) => {
                                return (
                                    <div className="flex items-center justify-end border-l">
                                        <OutlinedButton
                                            Icon={IconWrapper(CopyIcon)}
                                            label="Duplicate"
                                            marginLeft="2"
                                            className="py-3 bg-white"
                                            onClick={openFieldEdit({
                                                index,
                                                type: FieldModalType.New
                                            })}
                                        />
                                        <OutlinedButton
                                            Icon={IconWrapper(EditIcon)}
                                            label="Edit"
                                            marginLeft="2"
                                            className="py-3 bg-white"
                                            onClick={openFieldEdit({
                                                index,
                                                type: FieldModalType.Edit
                                            })}
                                        />
                                        <OutlinedButton
                                            testId="delete-field-button"
                                            label=""
                                            marginLeft="2"
                                            className="py-3 px-3 bg-white"
                                            Icon={IconWrapper(TrashIcon, true)}
                                            onClick={openFieldDelete(
                                                onFieldDelete(index)
                                            )}
                                        />
                                    </div>
                                )
                            }
                        }
                    ]}
                />
            </div>
            {showEditDocument && (
                <DocumentModal
                    schema={schema}
                    onSubmit={onDocumentSubmit}
                    onClose={closeEditDocumentModal}
                    value={{
                        documentType,
                        documentId
                    }}
                />
            )}
            {showFieldModal && (
                <FieldModal
                    type={fieldModalOptions.type}
                    schema={schema}
                    docType={documentType}
                    onSubmit={handleFieldModalSubmit}
                    onClose={closeFieldModal}
                    index={
                        fieldModalOptions.type === FieldModalType.Edit &&
                        fieldModalOptions.index !== undefined
                            ? fieldModalOptions.index
                            : annotationFields.length
                    }
                    value={
                        fieldModalOptions.index !== undefined
                            ? fieldModalOptions.type === FieldModalType.Edit
                                ? annotationFields[fieldModalOptions.index]
                                : {
                                      ...annotationFields[
                                          fieldModalOptions.index
                                      ],
                                      fieldId: {
                                          ...annotationFields[
                                              fieldModalOptions.index
                                          ].fieldId,
                                          data: randID()
                                      }
                                  }
                            : undefined
                    }
                />
            )}
            {showDeleteModal && (
                <SchemaFormDeleteModal
                    testId="delete-modal"
                    onClose={closeDeleteModal}
                    onSubmit={deleteHandler}
                />
            )}
        </div>
    )
}
